<template>
  <div>
    <h1 class="text-3xl font-bold text-gray-800 mb-2">Configurações</h1>
    <p class="text-gray-600 mb-8">Gerencie as configurações da sua conta e do sistema.</p>

    <!-- Tabs for System Admin -->
    <Tabs v-if="isSystemAdmin && isAccountAdmin" value="0">
      <TabList>
        <Tab value="0">Configurações da Conta</Tab>
        <Tab value="1">Configurações do Sistema</Tab>
      </TabList>
      <TabPanels>
        <TabPanel value="0">
          <div class="mt-6">
            <!-- <div class="flex items-center mb-6">
              <h2 class="text-xl font-semibold text-gray-700">Configurações da Conta</h2>
              <span class="ml-3 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded"
                >Conta Atual</span
              >
            </div> -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <AccountSettingsCards />
            </div>
          </div>
        </TabPanel>
        <TabPanel value="1">
          <div class="mt-6">
            <!-- <div class="flex items-center mb-6">
              <h2 class="text-xl font-semibold text-gray-700">Configurações do Sistema</h2>
              <span class="ml-3 px-2 py-1 text-xs font-medium bg-purple-100 text-purple-700 rounded"
                >Sistema</span
              >
            </div> -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <SystemSettingsCards />
            </div>

            <!-- Deployment Info -->
            <div class="mt-8 pt-4 border-t border-gray-200">
              <div class="relative inline-block group">
                <span class="text-xs text-gray-500 cursor-help">
                  Versão do Sistema: {{ deploymentInfo?.image_tag || 'Carregando...' }}
                </span>

                <div v-if="deploymentInfo && !loadingDeploymentInfo"
                     class="absolute bottom-full left-0 mb-2 w-80 bg-gray-900 text-white text-xs rounded-lg shadow-lg p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200 z-50">
                  <div class="space-y-2">
                    <div>
                      <span class="text-gray-400">Tag da Imagem:</span>
                      <div class="text-white font-mono mt-1">{{ deploymentInfo.image_tag || 'N/A' }}</div>
                    </div>
                    <div>
                      <span class="text-gray-400">Publicado em:</span>
                      <div class="text-white mt-1">{{ formattedDeploymentTime }}</div>
                    </div>
                  </div>
                  <div class="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                </div>
              </div>
            </div>
          </div>
        </TabPanel>
      </TabPanels>
    </Tabs>

    <!-- Simple view for non-System Admin -->
    <div v-else-if="isSystemAdmin">
      <div class="flex items-center mb-4">
        <h2 class="text-xl font-semibold text-gray-700">Configurações do Sistema</h2>
        <span class="ml-3 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded"
          >Conta Atual</span
        >
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <SystemSettingsCards />
      </div>
      <!-- Deployment Info -->
      <div class="mt-8 pt-4 border-t border-gray-200">
        <div class="relative inline-block group">
          <span class="text-xs text-gray-500 cursor-help">
            Versão do Sistema: {{ deploymentInfo?.image_tag || 'Carregando...' }}
          </span>

          <div v-if="deploymentInfo && !loadingDeploymentInfo"
                class="absolute bottom-full left-0 mb-2 w-80 bg-gray-900 text-white text-xs rounded-lg shadow-lg p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200 z-50">
            <div class="space-y-2">
              <div>
                <span class="text-gray-400">Tag da Imagem:</span>
                <div class="text-white font-mono mt-1">{{ deploymentInfo.image_tag || 'N/A' }}</div>
              </div>
              <div>
                <span class="text-gray-400">Publicado em:</span>
                <div class="text-white mt-1">{{ formattedDeploymentTime }}</div>
              </div>
            </div>
            <div class="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="isAccountAdmin">
      <div class="flex items-center mb-4">
        <h2 class="text-xl font-semibold text-gray-700">Configurações da Conta</h2>
        <span class="ml-3 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded"
          >Conta Atual</span
        >
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <AccountSettingsCards />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAuthStore } from '@/stores/auth'
import { ref, onMounted, computed } from 'vue';
import api from '@/api';
import Tabs from 'primevue/tabs'
import TabList from 'primevue/tablist'
import Tab from 'primevue/tab'
import TabPanels from 'primevue/tabpanels'
import TabPanel from 'primevue/tabpanel'
import AccountSettingsCards from '@/components/settings/AccountSettingsCards.vue'
import SystemSettingsCards from '@/components/settings/SystemSettingsCards.vue'
import { formatDate } from '@/utils/formatters';

interface DeploymentInfo {
  image_tag: string | null;
  deployment_timestamp: string | null;
}

const authStore = useAuthStore()
const isSystemAdmin = computed(() => authStore.isSystemAdmin)
const isAccountAdmin = computed(() => authStore.isAccountAdmin)

const deploymentInfo = ref<DeploymentInfo | null>(null);
const loadingDeploymentInfo = ref(true);
const deploymentInfoError = ref(false);

const formattedDeploymentTime = computed(() => {
  if (!deploymentInfo.value?.deployment_timestamp) {
    return 'N/A';
  }
  try {
    return formatDate(deploymentInfo.value.deployment_timestamp, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    }, deploymentInfo.value.deployment_timestamp);
  } catch (e) {
    console.log(e)
    return deploymentInfo.value.deployment_timestamp;
  }
});

onMounted(async () => {
  if (isSystemAdmin.value) {
    try {
      const data = await api.systemAdmin.deploymentInfo.get();
      deploymentInfo.value = data;
      deploymentInfoError.value = false;
    } catch (error) {
      console.error('Falha ao buscar informações de deploy:', error);
      deploymentInfoError.value = true;
    } finally {
      loadingDeploymentInfo.value = false;
    }
  }
});
</script>
